import React from 'react';
import { useHistory } from 'react-router-dom';
import { People } from '../hooks/api/useExperiences';
import { ROUTE_PATH } from '../routes/route-paths';
import { PhoneGrey26, PhoneWhite26, PeersonaGrey26, PeersonaWhite26 } from '../assets/images/svgs';
import { usePeersona } from '../contexts/peersona-context';
import useAuth from '../hooks/utils/useAuth';
import { AffiliationBanner, getAffiliationClientName } from './affiliation-banner';
import { ExperienceMatchHint, MatchStyleVariant } from './peer-listener-match-hint';

interface PeerAvatarProps {
  peer?: People;
  isPeersona?: boolean;
  forcePeersonaLabel?: boolean;
  isLoading?: boolean;
  /**
   * Opt-in for Fi-only surfaces: when ``true`` and the peer has a
   * ``chatbot_id``, the click opens the Peersona chat panel directly.
   * Defaults to ``false`` so every other surface (Browse, Favorites,
   * Home, peer-details rail) routes to the peer profile page.
   */
  opensPeersonaChatOnClick?: boolean;
  matchStyle?: MatchStyleVariant;
}

export const PeerAvatarSkeleton: React.FC = () => {
  return (
    <div className="flex w-[194px] flex-col items-start gap-[3px]">
      <div className="relative h-[150px] w-full overflow-hidden">
        <div className="absolute left-[22px] top-0 h-[150px] w-[150px] animate-pulse rounded-full bg-gray-200" />
      </div>
      <div className="flex min-h-14 w-full flex-col items-center justify-start">
        <div className="flex h-8 w-full flex-col items-center justify-center">
          <div className="h-6 w-3/4 animate-pulse rounded bg-gray-200" />
        </div>
        <div className="h-6 w-full" />
      </div>
    </div>
  );
};

export const PeerAvatar: React.FC<PeerAvatarProps> = ({
  peer,
  isPeersona = false,
  forcePeersonaLabel = false,
  isLoading = false,
  opensPeersonaChatOnClick = false,
  matchStyle = 'none',
}) => {
  const history = useHistory();
  const { openPanelForChatbot } = usePeersona();
  const { user } = useAuth();

  if (isLoading || !peer) {
    return <PeerAvatarSkeleton />;
  }

  const isListener = peer.is_listener;
  const isAvailable = peer.is_available;
  const opensPeersonaChat = isPeersona && opensPeersonaChatOnClick && peer.chatbot_id != null;
  const affiliationName = getAffiliationClientName(peer, user?.client_id);

  const handleClick = () => {
    if (opensPeersonaChat && peer.chatbot_id != null) {
      openPanelForChatbot(peer.chatbot_id);
      return;
    }
    history.push(`${ROUTE_PATH.PEER_DETAILS}?listenerId=${peer.listener_role_id}`);
  };

  return (
    <div className="relative flex w-[194px] cursor-pointer flex-col items-start gap-[3px]" onClick={handleClick}>
      {affiliationName && (
        <AffiliationBanner clientName={affiliationName} absolute={false} className="mb-1 rounded-md" />
      )}
      {/* Thumbnail Section */}
      <div className="relative h-[150px] w-full overflow-hidden">
        {/* Profile Image */}
        <div className="absolute left-[22px] top-0 h-[150px] w-[150px]">
          <img
            src={peer.profile_photo_url_square}
            alt={peer.user.display_name}
            className="border-avatar-border-slate h-full w-full rounded-full border object-cover"
          />
        </div>

        {/* Phone Icon Badge - Top Right */}
        {isListener && (
          <div className="absolute left-[148.32px] top-2 h-8 w-8">
            {/* Badge Circle */}
            <div
              className={`absolute inset-0 rounded-full ${
                isAvailable ? 'bg-success' : 'bg-unavailable'
              } border-2 border-white`}
            />
            {/* Phone Icon Inside Badge */}
            <div className="absolute inset-0 flex items-center justify-center">
              <img src={PhoneWhite26} alt="Phone" className="h-4 w-4" />
            </div>
          </div>
        )}

        {/* Peersona Badge - Top Right (if peersona) */}
        {isPeersona && (
          <div className="absolute left-[148.32px] top-2 h-8 w-8">
            {/* Badge Circle with peersona icon */}
            <div className="from-profile-gradient-start to-profile-gradient-end absolute inset-0 rounded-full border-2 border-white bg-gradient-to-br">
              <div className="absolute inset-0 flex items-center justify-center">
                <img src={PeersonaWhite26} alt="Peersona" className="h-5 w-5" />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Description Section */}
      <div className="flex min-h-14 w-full flex-col items-center justify-start">
        {/* Name */}
        <div className="flex h-8 w-full flex-col items-center justify-center">
          <p className="text-primary font-open-sans text-center text-base font-semibold leading-6">
            {peer.user.display_name}
          </p>
        </div>
        <ExperienceMatchHint match={peer} variant={matchStyle} />

        {/* Listener Badge */}
        {forcePeersonaLabel ? (
          <div className="flex w-full items-center justify-center gap-1.5">
            <div className="h-5 w-5 flex-shrink-0">
              <img src={PeersonaGrey26} alt="Peersona" className="h-5 w-5" />
            </div>
            <p className="font-open-sans text-section-header-slate text-sm font-semibold leading-6 tracking-[0.21px]">
              PEERSONA
            </p>
          </div>
        ) : isListener ? (
          <div className="flex w-full items-end justify-center gap-1">
            <div className="h-5 w-5 flex-shrink-0">
              <img src={PhoneGrey26} alt="Listener" className="h-5 w-5" />
            </div>
            <p className="font-open-sans text-section-header-slate text-sm font-semibold leading-6 tracking-[0.21px]">
              LISTENER
            </p>
          </div>
        ) : isPeersona ? (
          <div className="flex w-full items-center justify-center gap-1.5">
            <div className="h-5 w-5 flex-shrink-0">
              <img src={PeersonaGrey26} alt="Peersona" className="h-5 w-5" />
            </div>
            <p className="font-open-sans text-section-header-slate text-sm font-semibold leading-6 tracking-[0.21px]">
              PEERSONA
            </p>
          </div>
        ) : (
          <div className="h-6 w-full" />
        )}
      </div>
    </div>
  );
};
